// React
import { useEffect, useState } from "react";

// Third party
import clsx from "clsx";

// Material UI
import { Checkbox, FormControlLabel, Stack, SvgIcon, Typography } from "@mui/material";

// Components
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";
import SmartAddressFormSection from "@/Layouts/components/SmartAddressFormSection";
import TimezoneInput from "../StoreAddress/TimezoneInput";

// Helpers
import { and, or } from "@/Common/helpers/sonar";
import { isAllRequiredFieldSet } from "../StoreAddress/utils";

// Types
import { IGeoDetails, IStoreDetails } from "@/Common/interfaces/location";
import { TStoreAddress } from "../StoreAddress/types";

// Constants
import { INITIAL_STORE_ADDRESS } from "../StoreAddress/constants";
import { REQUIRED_FIELD_CLASSES } from "@/DataPages/components/CRM/SalesLeads/AddEditPopupDialog/constants";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/actions/trash.svg";

const sanitizeString = (str: string) => {
	return str.replace(/\s+/g, "").toLowerCase();
};

const isStoreNameAlreadyUsed = (otherStoreNames: string[], storeName: string) => {
	return otherStoreNames.some((name) => sanitizeString(name) === sanitizeString(storeName));
};

const isStoreEmailValid = (email: string) => {
	const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@([a-zA-Z0-9]+(-[a-zA-Z0-9]+)*\.)+[a-zA-Z]{2,}$/;
	return emailRegex.test(email.trim());
};

interface IStoreDetailsProps {
	index: number;
	storeDetails: IStoreDetails;
	isUpdateDisabled: boolean;
	geoDetails: IGeoDetails;
	timezone: string;
	otherStoreNames: string[];
	onRemoveStore: () => void;
	onSetDefaultStore: () => void;
	onUpdateStoreDetails: (index: number, newStoreDetails: IStoreDetails) => void;
	onUpdateStoreDetailsError: (hasError: boolean) => void;
}

const StoreDetails = (props: IStoreDetailsProps) => {
	const {
		index,
		storeDetails,
		geoDetails,
		timezone,
		isUpdateDisabled,
		otherStoreNames,
		onRemoveStore,
		onSetDefaultStore,
		onUpdateStoreDetails,
		onUpdateStoreDetailsError,
	} = props;

	const [geoLocation, setGeoLocation] = useState<GeoLocation>({ latitude: 0, longitude: 0 });
	const [errorName, setErrorName] = useState<boolean>(
		storeDetails.name ? isStoreNameAlreadyUsed(otherStoreNames, storeDetails.name) : false
	);
	const [errorEmail, setErrorEmail] = useState<boolean>(storeDetails.email ? !isStoreEmailValid(storeDetails.email) : false);

	/************************************************** HANDLERS ******************************************************/
	const handleNameBlur = (value: string) => {
		if (or(!value, isStoreNameAlreadyUsed(otherStoreNames, value))) {
			setErrorName(true);
			return;
		}
		setErrorName(false);
		onUpdateStoreDetails(index, { ...storeDetails, name: value });
	};

	const handleEmailBlur = (value: string) => {
		const isInvalidEmail = !isStoreEmailValid(value);
		if (or(!value, errorName, isInvalidEmail)) {
			setErrorEmail(true);
			return;
		}
		setErrorEmail(false);
		onUpdateStoreDetails(index, { ...storeDetails, email: value });
	};

	const handleChangeAddress = (newAddressInfo: ParsedAddress) => {
		const { location: storeGeoCoordinates } = newAddressInfo;

		const newAddress: IGeoDetails = {
			address: newAddressInfo.address1,
			address2: newAddressInfo?.address2 ?? "",
			city: newAddressInfo.city,
			state: newAddressInfo.stateCode,
			zipCode: newAddressInfo.zipCode,
			country: newAddressInfo.countryName,
			countryCode: newAddressInfo.countryCode,
		};

		if (!storeGeoCoordinates) {
			return;
		}
		setGeoLocation(storeGeoCoordinates);
		if (or(!isAllRequiredFieldSet(newAddress as TStoreAddress, ["address2"]), errorName, errorEmail)) {
			return;
		}
		onUpdateStoreDetails(index, { ...storeDetails, geoDetails: newAddress });
	};

	const handleSetTimezone = (value: string) => {
		if (or(!value, errorName, errorEmail, !isAllRequiredFieldSet(storeDetails.geoDetails as TStoreAddress, ["address2"]))) {
			return;
		}
		onUpdateStoreDetails(index, { ...storeDetails, timezone: value });
	};

	useEffect(() => {
		onUpdateStoreDetailsError(
			or(
				!storeDetails.name,
				!storeDetails.email,
				!isAllRequiredFieldSet(storeDetails.geoDetails as TStoreAddress, ["address2"]),
				!storeDetails.timezone,
				isStoreNameAlreadyUsed(otherStoreNames, storeDetails.name)
			)
		);
	}, [storeDetails, otherStoreNames]);

	/************************************************** RENDERING *****************************************************/

	return (
		<Stack flexDirection="column" gap={1}>
			<Stack flexDirection="row" gap={1} alignItems="center" justifyContent="space-between">
				<FormControlLabel
					control={
						<Checkbox
							checked={storeDetails.isDefaultStore}
							onChange={onSetDefaultStore}
							disabled={or(isUpdateDisabled, storeDetails.isDefaultStore, errorName)}
						/>
					}
					classes={{
						root: "!text-primary",
						label: clsx("!text-[14.5px] !font-sans !font-normal", {
							"!text-default": and(!isUpdateDisabled, !storeDetails.isDefaultStore, !errorName),
							"!text-disabled": or(isUpdateDisabled, storeDetails.isDefaultStore, errorName),
						}),
					}}
					label={"Set as Default"}
					labelPlacement="end"
				/>
				<DoubleCheckingButton
					title={"Remove Store Name and Address"}
					icon={<CloseRoundedIcon fontSize="small" />}
					iconHover={<CloseRoundedIcon fontSize="small" />}
					endIcon={<SvgIcon className="!text-[16px] ml-[4px] !text-white" component={TrashIcon} viewBox={"0 0 14 14"} />}
					mainButtonClasses={"btn-simple-remove"}
					disabled={isUpdateDisabled || storeDetails.isDefaultStore}
					confirmationSx={{ right: "0px !important", width: "230px !important" }}
					onDeleteAction={onRemoveStore}
				/>
			</Stack>
			<Stack flexDirection="column" gap={3}>
				<Stack flexDirection="column" gap={1}>
					<Typography className="!text-default !text-xl !font-montserrat !font-medium">Store Name / Email</Typography>
					<Stack flexDirection="row" width={"806px"} gap={2}>
						<Stack flexDirection={"column"} gap={1} flex={1}>
							<Typography className="!text-default !text-[14.5px] !font-sans !font-normal">Store Name</Typography>
							<SimpleTextField
								outlined
								editable={!isUpdateDisabled}
								text={storeDetails.name}
								placeholder={"Store Name"}
								error={!storeDetails.name || errorName}
								onActionUpdate={(value) => handleNameBlur(value)}
								disabled={isUpdateDisabled}
								classes={!storeDetails.name || errorName ? REQUIRED_FIELD_CLASSES : ""}
							/>
						</Stack>
						<Stack flexDirection={"column"} gap={1} flex={1}>
							<Typography className="!text-default !text-[14.5px] !font-sans !font-normal">Email</Typography>
							<SimpleTextField
								outlined
								editable={!isUpdateDisabled}
								text={storeDetails.email}
								placeholder={"Store Email"}
								error={!storeDetails.email || errorEmail}
								onActionUpdate={(value) => handleEmailBlur(value)}
								disabled={isUpdateDisabled}
								classes={!storeDetails.email || errorEmail ? REQUIRED_FIELD_CLASSES : ""}
							/>
						</Stack>
					</Stack>
				</Stack>
				<Stack flexDirection="column" gap={1}>
					<Typography className="!text-default !text-xl !font-montserrat !font-medium">Address</Typography>
					<Stack flexDirection="row" gap={2} alignItems={"flex-end"}>
						<Stack flexDirection="column" width={"806px"}>
							<SmartAddressFormSection
								key={"smart-address-form-section-admin-tools"}
								geoDetailValue={geoDetails}
								disabled={false}
								variant={"business"}
								requiredFields={true}
								onValidateAddressAction={(event) => handleChangeAddress(event as ParsedAddress)}
								onCallback={() => handleChangeAddress(INITIAL_STORE_ADDRESS)}
							/>
						</Stack>
						<Stack flexDirection="column" width={"172px"}>
							<TimezoneInput
								key={"timezone-input"}
								timezone={timezone}
								loading={false}
								required={true}
								geoLocation={geoLocation}
								setTimezone={handleSetTimezone}
							/>
						</Stack>
					</Stack>
				</Stack>
			</Stack>
		</Stack>
	);
};
export default StoreDetails;
